/**
 * The repository, pull request and issue operations (Phase 1).
 *
 * Split from `operations.ts` only for length; `operations.ts` re-exports
 * everything here, so the navigation test and every page see one surface.
 * The same rules apply: one function per `/v1` operation, paths checked
 * against the generated contract, and the pagination envelope normalised by
 * `page()`.
 */

import { expect, expectNoContent, http } from './client';
import type { components } from './gen/schema';

type Schemas = components['schemas'];

export type Organization = Schemas['Organization'];
export type Membership = Schemas['Membership'];
export type Repository = Schemas['Repository'];
export type Visibility = Schemas['Visibility'];
export type Collaborator = Schemas['Collaborator'];
export type RepoRole = Schemas['RepoRole'];
export type Branch = Schemas['Branch'];
export type Commit = Schemas['Commit'];
export type CommitDetail = Schemas['CommitDetail'];
export type ChangedFile = Schemas['ChangedFile'];
export type Diff = Schemas['Diff'];
export type Tree = Schemas['Tree'];
export type TreeEntry = Schemas['TreeEntry'];
export type Blob = Schemas['Blob'];
export type FilePatch = Schemas['FilePatch'];
export type PullRequest = Schemas['PullRequest'];
export type PullRequestState = Schemas['PullRequestState'];
export type Mergeability = Schemas['Mergeability'];
export type MergeStrategy = Schemas['MergeStrategy'];
export type MergeResult = Schemas['MergeResult'];
export type Review = Schemas['Review'];
export type ReviewState = Schemas['ReviewState'];
export type ReviewSummary = Schemas['ReviewSummary'];
export type ReviewThread = Schemas['ReviewThread'];
export type ReviewComment = Schemas['ReviewComment'];
export type Reviewer = Schemas['Reviewer'];
export type Issue = Schemas['Issue'];
export type IssueComment = Schemas['IssueComment'];
export type CloseReason = Schemas['CloseReason'];

export interface Paged<T> {
  data: T[];
  next_cursor: string | null;
}

function paged<T>(value: { data?: unknown; next_cursor?: unknown }): Paged<T> {
  return {
    data: (value.data ?? []) as T[],
    next_cursor: typeof value.next_cursor === 'string' ? value.next_cursor : null,
  };
}

interface RepoScope {
  orgId: string;
  repoId: string;
}

const repoPath = ({ orgId, repoId }: RepoScope) => ({ org_id: orgId, repo_id: repoId });

/* ====================================================================== *
 * Organizations
 * ====================================================================== */

/** Resolve one of the caller's organizations by slug; 404 unless a member. */
export function getMyOrganizationBySlug(slug: string): Promise<Organization> {
  return expect(http.GET('/users/me/organizations/{slug}', { params: { path: { slug } } }));
}

export function createOrganization(body: {
  slug: string;
  display_name: string;
  description?: string;
}): Promise<Organization> {
  return expect(http.POST('/organizations', { body }));
}

export async function listOrganizationMembers(orgId: string): Promise<Paged<Membership>> {
  return paged<Membership>(
    await expect(
      http.GET('/organizations/{org_id}/members', {
        params: { path: { org_id: orgId }, query: { limit: 100 } },
      }),
    ),
  );
}

export function addOrganizationMember(
  orgId: string,
  body: { username: string; role: Membership['role'] },
): Promise<void> {
  return expectNoContent(
    http.POST('/organizations/{org_id}/members', { params: { path: { org_id: orgId } }, body }),
  );
}

/* ====================================================================== *
 * Repositories
 * ====================================================================== */

export async function listRepositories(orgId: string): Promise<Paged<Repository>> {
  return paged<Repository>(
    await expect(
      http.GET('/organizations/{org_id}/repositories', {
        params: { path: { org_id: orgId }, query: { limit: 100 } },
      }),
    ),
  );
}

export function getRepository(scope: RepoScope): Promise<Repository> {
  return expect(
    http.GET('/organizations/{org_id}/repositories/{repo_id}', {
      params: { path: repoPath(scope) },
    }),
  );
}

export function createRepository(
  orgId: string,
  body: { name: string; description?: string; visibility?: Visibility; default_branch?: string },
): Promise<Repository> {
  return expect(
    http.POST('/organizations/{org_id}/repositories', {
      params: { path: { org_id: orgId } },
      body,
    }),
  );
}

export async function listRepositoryCollaborators(scope: RepoScope): Promise<Paged<Collaborator>> {
  return paged<Collaborator>(
    await expect(
      http.GET('/organizations/{org_id}/repositories/{repo_id}/collaborators', {
        params: { path: repoPath(scope) },
      }),
    ),
  );
}

export function setRepositoryCollaborator(
  scope: RepoScope,
  userId: string,
  role: RepoRole,
): Promise<Collaborator> {
  return expect(
    http.PUT('/organizations/{org_id}/repositories/{repo_id}/collaborators/{user_id}', {
      params: { path: { ...repoPath(scope), user_id: userId } },
      body: { role },
    }),
  );
}

export function removeRepositoryCollaborator(scope: RepoScope, userId: string): Promise<void> {
  return expectNoContent(
    http.DELETE('/organizations/{org_id}/repositories/{repo_id}/collaborators/{user_id}', {
      params: { path: { ...repoPath(scope), user_id: userId } },
    }),
  );
}

/* ====================================================================== *
 * Git content
 * ====================================================================== */

export async function listBranches(scope: RepoScope): Promise<Branch[]> {
  return paged<Branch>(
    await expect(
      http.GET('/organizations/{org_id}/repositories/{repo_id}/branches', {
        params: { path: repoPath(scope) },
      }),
    ),
  ).data;
}

export async function listCommits(
  scope: RepoScope,
  options: { ref?: string; path?: string; cursor?: string; limit?: number } = {},
): Promise<Paged<Commit>> {
  return paged<Commit>(
    await expect(
      http.GET('/organizations/{org_id}/repositories/{repo_id}/commits', {
        params: {
          path: repoPath(scope),
          query: {
            limit: options.limit ?? 30,
            ...(options.ref ? { ref: options.ref } : {}),
            ...(options.path ? { path: options.path } : {}),
            ...(options.cursor ? { cursor: options.cursor } : {}),
          },
        },
      }),
    ),
  );
}

export function getCommit(scope: RepoScope, sha: string): Promise<CommitDetail> {
  return expect(
    http.GET('/organizations/{org_id}/repositories/{repo_id}/commits/{sha}', {
      params: { path: { ...repoPath(scope), sha } },
    }),
  );
}

export function getTree(scope: RepoScope, options: { ref?: string; path?: string }): Promise<Tree> {
  return expect(
    http.GET('/organizations/{org_id}/repositories/{repo_id}/tree', {
      params: {
        path: repoPath(scope),
        query: {
          ...(options.ref ? { ref: options.ref } : {}),
          ...(options.path ? { path: options.path } : {}),
        },
      },
    }),
  );
}

export function getBlob(scope: RepoScope, options: { ref?: string; path: string }): Promise<Blob> {
  return expect(
    http.GET('/organizations/{org_id}/repositories/{repo_id}/blob', {
      params: {
        path: repoPath(scope),
        query: { path: options.path, ...(options.ref ? { ref: options.ref } : {}) },
      },
    }),
  );
}

export function compareRevisions(scope: RepoScope, base: string, head: string): Promise<Diff> {
  return expect(
    http.GET('/organizations/{org_id}/repositories/{repo_id}/compare', {
      params: { path: repoPath(scope), query: { base, head } },
    }),
  );
}

export function getFilePatch(
  scope: RepoScope,
  options: { base: string; head: string; path: string; oldPath?: string | undefined },
): Promise<FilePatch> {
  return expect(
    http.GET('/organizations/{org_id}/repositories/{repo_id}/patch', {
      params: {
        path: repoPath(scope),
        query: {
          head: options.head,
          path: options.path,
          ...(options.base ? { base: options.base } : {}),
          ...(options.oldPath ? { old_path: options.oldPath } : {}),
        },
      },
    }),
  );
}

/* ====================================================================== *
 * Pull requests
 * ====================================================================== */

const pullPath = (scope: RepoScope, number: number) => ({ ...repoPath(scope), number });

export async function listPullRequests(
  scope: RepoScope,
  state?: PullRequestState,
): Promise<Paged<PullRequest>> {
  return paged<PullRequest>(
    await expect(
      http.GET('/organizations/{org_id}/repositories/{repo_id}/pull-requests', {
        params: { path: repoPath(scope), query: { limit: 50, ...(state ? { state } : {}) } },
      }),
    ),
  );
}

export function createPullRequest(
  scope: RepoScope,
  body: {
    source_branch: string;
    target_branch: string;
    title: string;
    description?: string;
    draft: boolean;
  },
): Promise<PullRequest> {
  return expect(
    http.POST('/organizations/{org_id}/repositories/{repo_id}/pull-requests', {
      params: { path: repoPath(scope) },
      body,
    }),
  );
}

export function getPullRequest(scope: RepoScope, number: number): Promise<PullRequest> {
  return expect(
    http.GET('/organizations/{org_id}/repositories/{repo_id}/pull-requests/{number}', {
      params: { path: pullPath(scope, number) },
    }),
  );
}

export function listPullRequestFiles(scope: RepoScope, number: number): Promise<Diff> {
  return expect(
    http.GET('/organizations/{org_id}/repositories/{repo_id}/pull-requests/{number}/files', {
      params: { path: pullPath(scope, number) },
    }),
  );
}

export async function listPullRequestCommits(
  scope: RepoScope,
  number: number,
): Promise<Paged<Commit>> {
  return paged<Commit>(
    await expect(
      http.GET('/organizations/{org_id}/repositories/{repo_id}/pull-requests/{number}/commits', {
        params: { path: pullPath(scope, number), query: { limit: 100 } },
      }),
    ),
  );
}

export function getPullRequestMergeability(
  scope: RepoScope,
  number: number,
): Promise<Mergeability> {
  return expect(
    http.GET('/organizations/{org_id}/repositories/{repo_id}/pull-requests/{number}/mergeability', {
      params: { path: pullPath(scope, number) },
    }),
  );
}

export function mergePullRequest(
  scope: RepoScope,
  number: number,
  body: { strategy: MergeStrategy; expected_source?: string; message?: string },
): Promise<MergeResult> {
  return expect(
    http.POST('/organizations/{org_id}/repositories/{repo_id}/pull-requests/{number}/merge', {
      params: { path: pullPath(scope, number) },
      body,
    }),
  );
}

export function closePullRequest(
  scope: RepoScope,
  number: number,
  ifVersion: number,
): Promise<PullRequest> {
  return expect(
    http.POST('/organizations/{org_id}/repositories/{repo_id}/pull-requests/{number}/close', {
      params: { path: pullPath(scope, number) },
      body: { if_version: ifVersion },
    }),
  );
}

export function reopenPullRequest(
  scope: RepoScope,
  number: number,
  ifVersion: number,
): Promise<PullRequest> {
  return expect(
    http.POST('/organizations/{org_id}/repositories/{repo_id}/pull-requests/{number}/reopen', {
      params: { path: pullPath(scope, number) },
      body: { if_version: ifVersion },
    }),
  );
}

export function markPullRequestReady(
  scope: RepoScope,
  number: number,
  ifVersion: number,
): Promise<PullRequest> {
  return expect(
    http.POST('/organizations/{org_id}/repositories/{repo_id}/pull-requests/{number}/ready', {
      params: { path: pullPath(scope, number) },
      body: { if_version: ifVersion },
    }),
  );
}

export async function listPullRequestReviewers(
  scope: RepoScope,
  number: number,
): Promise<Reviewer[]> {
  return paged<Reviewer>(
    await expect(
      http.GET('/organizations/{org_id}/repositories/{repo_id}/pull-requests/{number}/reviewers', {
        params: { path: pullPath(scope, number) },
      }),
    ),
  ).data;
}

export function assignPullRequestReviewer(
  scope: RepoScope,
  number: number,
  userId: string,
): Promise<Reviewer> {
  return expect(
    http.POST('/organizations/{org_id}/repositories/{repo_id}/pull-requests/{number}/reviewers', {
      params: { path: pullPath(scope, number) },
      body: { user_id: userId },
    }),
  );
}

export async function listPullRequestReviews(scope: RepoScope, number: number): Promise<Review[]> {
  return paged<Review>(
    await expect(
      http.GET('/organizations/{org_id}/repositories/{repo_id}/pull-requests/{number}/reviews', {
        params: { path: pullPath(scope, number) },
      }),
    ),
  ).data;
}

export function submitPullRequestReview(
  scope: RepoScope,
  number: number,
  body: { state: ReviewState; body?: string },
): Promise<Review> {
  return expect(
    http.POST('/organizations/{org_id}/repositories/{repo_id}/pull-requests/{number}/reviews', {
      params: { path: pullPath(scope, number) },
      body,
    }),
  );
}

export function getPullRequestReviewSummary(
  scope: RepoScope,
  number: number,
): Promise<ReviewSummary> {
  return expect(
    http.GET(
      '/organizations/{org_id}/repositories/{repo_id}/pull-requests/{number}/review-summary',
      { params: { path: pullPath(scope, number) } },
    ),
  );
}

export async function listReviewThreads(scope: RepoScope, number: number): Promise<ReviewThread[]> {
  return paged<ReviewThread>(
    await expect(
      http.GET('/organizations/{org_id}/repositories/{repo_id}/pull-requests/{number}/threads', {
        params: { path: pullPath(scope, number), query: { limit: 100 } },
      }),
    ),
  ).data;
}

export function createReviewThread(
  scope: RepoScope,
  number: number,
  body: {
    kind: 'file' | 'inline';
    path: string;
    commit: string;
    side?: 'old' | 'new';
    start_line?: number;
    body: string;
  },
): Promise<ReviewThread> {
  return expect(
    http.POST('/organizations/{org_id}/repositories/{repo_id}/pull-requests/{number}/threads', {
      params: { path: pullPath(scope, number) },
      body,
    }),
  );
}

export function replyToReviewThread(
  scope: RepoScope,
  number: number,
  threadId: string,
  body: string,
): Promise<ReviewComment> {
  return expect(
    http.POST(
      '/organizations/{org_id}/repositories/{repo_id}/pull-requests/{number}/threads/{thread_id}/replies',
      { params: { path: { ...pullPath(scope, number), thread_id: threadId } }, body: { body } },
    ),
  );
}

export function resolveReviewThread(
  scope: RepoScope,
  number: number,
  threadId: string,
  ifVersion: number,
): Promise<ReviewThread> {
  return expect(
    http.POST(
      '/organizations/{org_id}/repositories/{repo_id}/pull-requests/{number}/threads/{thread_id}/resolve',
      {
        params: { path: { ...pullPath(scope, number), thread_id: threadId } },
        body: { if_version: ifVersion },
      },
    ),
  );
}

/* ====================================================================== *
 * Issues
 * ====================================================================== */

export async function listIssues(
  scope: RepoScope,
  state?: 'open' | 'closed',
): Promise<Paged<Issue>> {
  return paged<Issue>(
    await expect(
      http.GET('/organizations/{org_id}/repositories/{repo_id}/issues', {
        params: { path: repoPath(scope), query: { limit: 50, ...(state ? { state } : {}) } },
      }),
    ),
  );
}

export function createIssue(
  scope: RepoScope,
  body: { title: string; body?: string },
): Promise<Issue> {
  return expect(
    http.POST('/organizations/{org_id}/repositories/{repo_id}/issues', {
      params: { path: repoPath(scope) },
      body,
    }),
  );
}

export function getIssue(scope: RepoScope, number: number): Promise<Issue> {
  return expect(
    http.GET('/organizations/{org_id}/repositories/{repo_id}/issues/{number}', {
      params: { path: pullPath(scope, number) },
    }),
  );
}

export function updateIssue(
  scope: RepoScope,
  number: number,
  body: { title?: string; body?: string; if_version: number },
): Promise<Issue> {
  return expect(
    http.PATCH('/organizations/{org_id}/repositories/{repo_id}/issues/{number}', {
      params: { path: pullPath(scope, number) },
      body,
    }),
  );
}

export function closeIssue(
  scope: RepoScope,
  number: number,
  closeReason: CloseReason,
  ifVersion: number,
): Promise<Issue> {
  return expect(
    http.POST('/organizations/{org_id}/repositories/{repo_id}/issues/{number}/close', {
      params: { path: pullPath(scope, number) },
      body: { close_reason: closeReason, if_version: ifVersion },
    }),
  );
}

export function reopenIssue(scope: RepoScope, number: number, ifVersion: number): Promise<Issue> {
  return expect(
    http.POST('/organizations/{org_id}/repositories/{repo_id}/issues/{number}/reopen', {
      params: { path: pullPath(scope, number) },
      body: { if_version: ifVersion },
    }),
  );
}

export async function listIssueComments(scope: RepoScope, number: number): Promise<IssueComment[]> {
  return paged<IssueComment>(
    await expect(
      http.GET('/organizations/{org_id}/repositories/{repo_id}/issues/{number}/comments', {
        params: { path: pullPath(scope, number), query: { limit: 100 } },
      }),
    ),
  ).data;
}

export function createIssueComment(
  scope: RepoScope,
  number: number,
  body: string,
): Promise<IssueComment> {
  return expect(
    http.POST('/organizations/{org_id}/repositories/{repo_id}/issues/{number}/comments', {
      params: { path: pullPath(scope, number) },
      body: { body },
    }),
  );
}

/* ====================================================================== *
 * Access tokens
 *
 * The credential a Git client presents (in the Basic-auth password field).
 * The plaintext comes back exactly once, from create; nothing can read it
 * again, so the interface shows it once and says so.
 * ====================================================================== */

export type TokenCreated = Schemas['TokenCreated'];

export function createToken(body: {
  name: string;
  scopes: string[];
  expires_in_days: number;
}): Promise<TokenCreated> {
  return expect(http.POST('/users/me/tokens', { body }));
}

export function revokeToken(tokenId: string): Promise<void> {
  return expectNoContent(
    http.DELETE('/users/me/tokens/{token_id}', { params: { path: { token_id: tokenId } } }),
  );
}
